/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import { BsShieldCheck, BsShieldX, BsInfoCircle } from 'react-icons/bs';
import { formatCurrency } from 'react-data-formatter';

import api from 'api';
import { Header, Button, ConfirmDialog } from 'components';
import BackofficeMenu from 'components/BackofficeMenu';
import {
  Container,
  ContentArea,
  Title,
  LoadingArea,
  PolicyInformationArea,
  ItemPolicy,
  Subtitle,
  Text,
  CoveragesArea,
  ProductItem,
  ValueComparison,
  ValueCard,
  Footer,
} from './styles';

function Endorsement({ match }) {
  const history = useHistory();
  const policyId = match.params.id;

  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [policyData, setPolicyData] = useState(null);
  const [quotationData, setQuotationData] = useState(null);
  const [quotationResponse, setQuotationResponse] = useState(null);
  const [initialValue, setInitialValue] = useState(0);

  const [availableCoverages, setAvailableCoverages] = useState({
    basic: [],
    additional: [],
    lmi: [],
    deductible: [],
  });

  const [selectedProducts, setSelectedProducts] = useState({
    basic: null,
    support: null,
    glass: null,
    replacement: null,
    lmi: null,
    deductible: null,
    optionals: [],
  });
  const [openConfirm, setOpenConfirm] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await api({
        method: 'GET',
        url: `/splitrisk-policy/${policyId}`,
        headers: {
          Authorization: Cookies.get('splitrisk-authtoken'),
        },
      });

      const policy = resp.data;
      setPolicyData(policy);
      setInitialValue(policy?.value || 0);

      const initialQuotation = {
        vehicle: {
          id_brand: policy?.vehicle?.id_brand,
          fipe_code: policy?.vehicle?.fipe_code,
          value: policy?.vehicle?.value,
          id_type: policy?.vehicle?.id_vehicle_type,
          zero_km: policy?.vehicle?.zero_km,
          id_vehicle_use: policy?.vehicle?.id_vehicle_use,
          manufacture_year: policy?.vehicle?.manufacture_year,
          year_model: policy?.vehicle?.year_model,
          gnv: policy?.vehicle?.gnv,
          eletric_hybrid: policy?.vehicle?.eletric_hybrid,
          some_driver_18_25_years_old: policy?.vehicle?.some_driver_18_25_years_old,
        },
        quotationProposalData: {
          quotationProposalId: policy?.TB_PROPOSAL?.id,
        },
        address: policy?.TB_ADDRESS,
        policy_modality: policy?.modality,
        comission: policy?.TB_PROPOSAL?.comission || 0.1, // Manter fixo
      };

      setQuotationData(initialQuotation);

      // Carregar produtos atuais
      const qp = policy?.TB_PROPOSAL?.TB_QUOTATION_PROPOSAL?.[0];
      const initialSelection = {
        basic: qp?.TB_PRICING_BASIC_COVERAGE_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_basic_coverage || null,
        support: qp?.TB_PRICING_SUPPORT_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_support || null,
        glass: qp?.TB_PRICING_GLASS_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_glass || null,
        replacement: qp?.TB_PRICING_REPLACEMENT_CAR_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_replacement_car || null,
        lmi: qp?.TB_PRICING_FIPE_LMI_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_fipe_lmi || null,
        deductible: qp?.TB_PRICING_DEDUCTIBLE_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_deductible || null,
        optionals: qp?.TB_PRICING_OPTIONAL_COVERAGE_TB_QUOTATION_PROPOSAL?.map((i) => i.id_pricing_optional_coverage) || [],
      };
      setSelectedProducts(initialSelection);

      // Carregar todas as coberturas disponíveis
      await loadAvailableCoverages(policy);

      // Primeira cotação para bater os valores
      await handleQuotation(initialQuotation, initialSelection);

      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados do contrato');
      setLoading(false);
    }
  };

  const loadAvailableCoverages = async (policy) => {
    try {
      const vehicle = policy?.vehicle;
      const isMotorcycle = [4, 7].includes(vehicle?.id_vehicle_type);
      const motorType = isMotorcycle ? 2 : 1;

      const commonData = {
        fipe_code: vehicle?.fipe_code,
        brand: vehicle?.id_brand,
        value: vehicle?.value,
        type_vehicle: motorType,
        zero_km: vehicle?.zero_km,
        cep: policy?.TB_ADDRESS?.cep || policy?.TB_ADDRESS?.zip_code,
        exclusive: false,
      };

      const [basic, support, replacement, glass, optional, lmi, deductible] = await Promise.all([
        api({
          method: 'POST',
          url: '/quotation/get-basic-coverage',
          data: { fipe_code: vehicle?.fipe_code, brand: vehicle?.id_brand, value: vehicle?.value },
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api({
          method: 'POST',
          url: '/quotation/get-support-coverage',
          data: commonData,
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api({
          method: 'POST',
          url: '/quotation/get-replacement-car-coverage',
          data: commonData,
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api({
          method: 'POST',
          url: '/quotation/get-glass-coverage',
          data: commonData,
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api({
          method: 'POST',
          url: '/quotation/get-optional-coverage',
          data: commonData,
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api({
          method: 'POST',
          url: '/quotation/get-fipe-lmi-coverage',
          data: { ...commonData, from_auction: false },
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api({
          method: 'POST',
          url: '/quotation/get-deductible-coverage',
          data: commonData,
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
      ]);

      setAvailableCoverages({
        basic: basic.data || [],
        additional: support.data || [],
        optional: optional.data || [],
        glass: glass.data || [],
        replacement: replacement.data || [],
        lmi: lmi.data || [],
        deductible: deductible.data || [],
      });
    } catch (error) {
      console.error('Erro ao carregar coberturas disponiveis:', error);
      toast.error('Erro ao listar coberturas do motor');
    }
  };

  const handleQuotation = async (baseData, products) => {
    if (!products || products.length === 0) return;

    setCalculating(true);
    try {
      const response = await api({
        method: 'POST',
        url: '/endorsement/quotation-preview',
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitrisk-authtoken'),
        },
        data: {
          ...baseData,
          products,
        },
      });
      setQuotationResponse(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao realizar o recalculo');
    } finally {
      setCalculating(false);
    }
  };

  const toggleProduct = (category, productId) => {
    let newSelection = { ...selectedProducts };

    if (category === 'optionals') {
      const isSelected = selectedProducts.optionals.includes(productId);
      if (isSelected) {
        newSelection.optionals = selectedProducts.optionals.filter((id) => id !== productId);
      } else {
        newSelection.optionals = [...selectedProducts.optionals, productId];
      }
    } else {
      // Toggle single selection (Básica, Vidros, etc.)
      newSelection[category] = selectedProducts[category] === productId ? null : productId;
    }

    setSelectedProducts(newSelection);
    handleQuotation(quotationData, newSelection);
  };

  const handleSaveAttempt = () => {
    setOpenConfirm(true);
  };

  const handleConfirmSave = async () => {
    try {
      setCalculating(true);
      await api({
        method: 'POST',
        url: `/refactor-quotation-value-proposal/${policyData?.TB_PROPOSAL?.id}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitrisk-authtoken'),
        },
        data: {
          products: selectedProducts,
          value: quotationResponse?.value,
        },
      });

      toast.success('Alteração de coberturas realizada com sucesso!');
      history.push(`/backoffice/detalhes-do-contrato/${policyId}`);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar as alterações');
    } finally {
      setCalculating(false);
      setOpenConfirm(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [policyId]);

  if (loading) {
    return (
      <>
        <Header />
        <Container>
          <LoadingArea>
            <ReactLoading color="rgb(0 0 204)" height={40} width={40} type="spin" />
          </LoadingArea>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container>
        <ContentArea>
          <BackofficeMenu policyId={policyId} policyData={policyData} />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
            <Title>ENDOSSO DE COBERTURAS</Title>
          </div>

          <PolicyInformationArea>
            {/* 🔹 LINHA COM OS 2 CARDS */}
            <div
              style={{
                display: 'flex',
                gap: 20,
                flexWrap: 'wrap',
                width: '100%', // 🔥 importante
              }}
            >
              <ItemPolicy style={{ flex: 1, minWidth: 300 }}>
                <Subtitle>Dados do Contrato</Subtitle>
                <Text>
                  Contrato: <span>#{policyId}</span>
                </Text>
                <Text>
                  Placa: <span>{policyData?.vehicle?.plate}</span>
                </Text>
                <Text>
                  Veículo:{' '}
                  <span>
                    {policyData?.vehicle?.brand} {policyData?.vehicle?.model}
                  </span>
                </Text>
                <Text>
                  Modalidade: <span>{policyData?.modality === 'monthly' ? 'Mensal' : 'Anual'}</span>
                </Text>
              </ItemPolicy>

              <ItemPolicy style={{ flex: 1, minWidth: 300 }}>
                <Subtitle>Resumo Financeiro</Subtitle>
                <ValueComparison>
                  <ValueCard>
                    <label>Valor Atual</label>
                    <span>{formatCurrency(initialValue)}</span>
                  </ValueCard>

                  <div style={{ fontSize: 24, color: '#ccc' }}>→</div>

                  <ValueCard active variant={quotationResponse?.value > initialValue ? 'error' : 'success'}>
                    <label>Novo Valor Estimado</label>
                    <span>{calculating ? '...' : formatCurrency(quotationResponse?.value || 0)}</span>
                  </ValueCard>
                </ValueComparison>

                <Text style={{ textAlign: 'center', fontSize: 12 }}>* Valores baseados nas coberturas selecionadas abaixo.</Text>
              </ItemPolicy>
            </div>

            <ItemPolicy fullWidth>
              <Subtitle>
                <BsShieldCheck size={20} style={{ marginRight: 10 }} /> Seleção de Coberturas
              </Subtitle>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
                <div>
                  <Text style={{ fontWeight: 'bold', marginBottom: 10, color: '#444' }}>Básicas</Text>
                  <CoveragesArea>
                    {availableCoverages.basic.map((item) => (
                      <ProductItem
                        key={item.id}
                        selected={selectedProducts.basic === item.id}
                        onClick={() => toggleProduct('basic', item.id)}
                      >
                        <div className="info">
                          {selectedProducts.basic === item.id ? <BsShieldCheck /> : <BsShieldX />}
                          <span>{item.name}</span>
                        </div>
                        <div className="value">
                          {item.ebaotech?.SumInsured
                            ? formatCurrency(item.ebaotech.SumInsured)
                            : item.lmi
                              ? formatCurrency(item.lmi)
                              : item.value
                                ? formatCurrency(item.value)
                                : ''}
                        </div>
                      </ProductItem>
                    ))}
                  </CoveragesArea>

                  {availableCoverages.lmi.length > 0 && (
                    <>
                      <Text style={{ fontWeight: 'bold', margin: '20px 0 10px', color: '#444' }}>Cotação FIPE</Text>
                      <CoveragesArea>
                        {availableCoverages.lmi.map((item) => (
                          <ProductItem
                            key={item.id}
                            selected={selectedProducts.lmi === item.id}
                            onClick={() => toggleProduct('lmi', item.id)}
                          >
                            <div className="info">
                              {selectedProducts.lmi === item.id ? <BsShieldCheck /> : <BsShieldX />}
                              <span>{item.name}</span>
                            </div>
                            <div className="value">{item.lmi ? `${item.lmi}%` : ''}</div>
                          </ProductItem>
                        ))}
                      </CoveragesArea>
                    </>
                  )}
                </div>

                <div>
                  <Text style={{ fontWeight: 'bold', marginBottom: 10, color: '#444' }}>Assistência</Text>
                  <CoveragesArea>
                    {availableCoverages.additional.map((item) => (
                      <ProductItem
                        key={item.id}
                        selected={selectedProducts.support === item.id}
                        onClick={() => toggleProduct('support', item.id)}
                      >
                        <div className="info">
                          {selectedProducts.support === item.id ? <BsShieldCheck /> : <BsShieldX />}
                          <span>{item.name}</span>
                        </div>
                        <div className="value">
                          {item.lmi ? formatCurrency(item.lmi) : item.value ? formatCurrency(item.value) : ''}
                        </div>
                      </ProductItem>
                    ))}
                  </CoveragesArea>

                  <Text style={{ fontWeight: 'bold', margin: '20px 0 10px', color: '#444' }}>Franquias</Text>
                  <CoveragesArea>
                    {availableCoverages.deductible.map((item) => (
                      <ProductItem
                        key={item.id}
                        selected={selectedProducts.deductible === item.id}
                        onClick={() => toggleProduct('deductible', item.id)}
                      >
                        <div className="info">
                          {selectedProducts.deductible === item.id ? <BsShieldCheck /> : <BsShieldX />}
                          <span>{item.name}</span>
                        </div>
                        <div className="value">{item.percent ? `${item.percent}%` : ''}</div>
                      </ProductItem>
                    ))}
                  </CoveragesArea>
                </div>

                {![4, 7].includes(policyData?.vehicle?.id_vehicle_type) && (
                  <>
                    <div>
                      <Text style={{ fontWeight: 'bold', marginBottom: 10, color: '#444' }}>Vidros</Text>
                      <CoveragesArea>
                        {availableCoverages.glass.map((item) => (
                          <ProductItem
                            key={item.id}
                            selected={selectedProducts.glass === item.id}
                            onClick={() => toggleProduct('glass', item.id)}
                          >
                            <div className="info">
                              {selectedProducts.glass === item.id ? <BsShieldCheck /> : <BsShieldX />}
                              <span>{item.name}</span>
                            </div>
                            <div className="value">{item.value ? formatCurrency(item.value) : ''}</div>
                          </ProductItem>
                        ))}
                      </CoveragesArea>

                      <Text style={{ fontWeight: 'bold', margin: '20px 0 10px', color: '#444' }}>Carro Reserva</Text>
                      <CoveragesArea>
                        {availableCoverages.replacement.map((item) => (
                          <ProductItem
                            key={item.id}
                            selected={selectedProducts.replacement === item.id}
                            onClick={() => toggleProduct('replacement', item.id)}
                          >
                            <div className="info">
                              {selectedProducts.replacement === item.id ? <BsShieldCheck /> : <BsShieldX />}
                              <span>{item.name}</span>
                            </div>
                            <div className="value">{item.value ? formatCurrency(item.value) : ''}</div>
                          </ProductItem>
                        ))}
                      </CoveragesArea>
                    </div>
                  </>
                )}

                <div>
                  <Text style={{ fontWeight: 'bold', marginBottom: 10, color: '#444' }}>Opcionais</Text>
                  <CoveragesArea>
                    {availableCoverages.optional.map((item) => (
                      <ProductItem
                        key={item.id}
                        selected={selectedProducts.optionals.includes(item.id)}
                        onClick={() => toggleProduct('optionals', item.id)}
                      >
                        <div className="info">
                          {selectedProducts.optionals.includes(item.id) ? <BsShieldCheck /> : <BsShieldX />}
                          <span>{item.name}</span>
                        </div>
                        <div className="value">
                          {item.lmi ? formatCurrency(item.lmi) : item.value ? formatCurrency(item.value) : ''}
                        </div>
                      </ProductItem>
                    ))}
                  </CoveragesArea>
                </div>
              </div>
            </ItemPolicy>
          </PolicyInformationArea>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button
              height="35px"
              style={{ background: 'transparent', border: '1px solid #ccc', color: '#666' }}
              onClick={() => history.push(`/backoffice/detalhes-do-contrato/${policyId}`)}
            >
              Cancelar
            </Button>
            <Button height="35px" loading={calculating} onClick={handleSaveAttempt}>
              Salvar Alterações
            </Button>
          </div>

          <Footer>
            <p style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#666', fontSize: 13 }}>
              <BsInfoCircle /> Selecione ou remova as coberturas desejadas para recalcular o valor do prêmio.
            </p>
          </Footer>
        </ContentArea>
      </Container>

      <ConfirmDialog
        open={openConfirm}
        title="Confirmar Alteração de Coberturas"
        content={`O valor do contrato será alterado para ${formatCurrency(
          quotationResponse?.value || 0,
        )}. Deseja confirmar esta operação?`}
        onConfirm={handleConfirmSave}
        onCancel={() => setOpenConfirm(false)}
        loading={calculating}
        centered={true}
      />
    </>
  );
}

export default Endorsement;
